// 명단을 다시 올릴 때 기존 학생과 비교해 "새로 만들 것·바뀐 칸만 고칠 것·그대로 둘 것"을 정한다(순수 함수 — 화면·DB 없음).
// 사장이 주소 검토·택배 전환·메모로 고친 값은 파일이 덮지 않는다. 파일이 주인인 칸(보호자·전화·원본 주소·수량)만 본다.
import type { Student } from "@/types";

/** 파일(학교 명단)이 주인인 칸 — 이 칸이 달라졌을 때만 기존 학생을 고친다 */
export const FILE_FIELDS = ["guardian", "phone", "rawAddress", "boxesPerMonth"] as const;
export type FileField = (typeof FILE_FIELDS)[number];

/** 원본 주소가 바뀌었을 때 파일 값으로 같이 다시 쓰는 파생 칸(글자) */
const ADDRESS_TEXT_FIELDS = ["refinedAddress", "postalCode", "gu", "dong", "buildingName", "buildingDong", "buildingHo"] as const;
/** 원본 주소가 바뀌면 옛 주소의 지오코딩 부가 정보는 지운다(새 주소와 섞이면 안 된다). 좌표는 파일 행에 있으면 새로 쓰고 없으면 같이 지운다 — 옛 집 좌표로 배송 가면 안 된다 */
const ADDRESS_GEO_EXTRA_FIELDS = ["geocodedAt", "geocodeNeedsCheck", "geocodeReasoning", "geocodeOriginalAddress", "geocodeCandidates", "geocodeAssumptions"] as const;
const ADDRESS_GEO_FIELDS = ["lat", "lng", "geocodeStatus", ...ADDRESS_GEO_EXTRA_FIELDS] as const;

export interface FieldChange {
  field: FileField;
  before: unknown;
  after: unknown;
}

export interface RowPlan {
  action: "create" | "update" | "same";
  changes: FieldChange[];
  addressChanged: boolean;
}

// 하이픈은 남긴다 — "온천동 12-3"과 "온천동 123"은 다른 집이다(코덱스 검토)
export const normAddr = (s?: string | null): string => (s || "").replace(/[\s,.()]/g, "");
export const normPhone = (s?: string | null): string => (s || "").replace(/\D/g, "");

/** 같은 학생인가 — 전화가 같거나 원본 주소(호수가 남는 쪽)가 같으면 같은 학생으로 본다(동명이인 함정 #5) */
export function isSameStudent(file: Partial<Student>, existing: Partial<Student>): boolean {
  const p1 = normPhone(file.phone);
  const p2 = normPhone(existing.phone);
  if (p1 && p2 && p1 === p2) return true;
  const a1 = normAddr(file.rawAddress || file.refinedAddress);
  const a2 = normAddr(existing.rawAddress || existing.refinedAddress);
  return !!a1 && a1 === a2;
}

/** 학교+이름이 같은 기존 학생들 중에서 같은 학생을 고른다. 없으면 null */
export function matchExisting<T extends Partial<Student>>(file: Partial<Student>, candidates: T[]): T | null {
  return candidates.find((c) => isSameStudent(file, c)) || null;
}

function fieldEqual(field: FileField, a: unknown, b: unknown): boolean {
  if (field === "phone") return normPhone(a as string) === normPhone(b as string);
  if (field === "rawAddress") return normAddr(a as string) === normAddr(b as string);
  if (field === "boxesPerMonth") return Number(a ?? 1) === Number(b ?? 1);
  return String(a ?? "").trim() === String(b ?? "").trim();
}

/** 파일 행과 기존 문서를 비교한다. existing이 없으면 새로 만든다 */
export function diffRow(file: Partial<Student>, existing: Partial<Student> | null): RowPlan {
  if (!existing) return { action: "create", changes: [], addressChanged: false };
  const changes: FieldChange[] = [];
  for (const f of FILE_FIELDS) {
    const after = file[f];
    // 파일에 그 칸이 아예 없으면(빈 값) 기존 값을 지우지 않는다 — 학교 명단이 전화를 빼먹었다고 전화가 사라지면 안 된다
    if (after === undefined || after === null || after === "") continue;
    if (!fieldEqual(f, after, existing[f])) changes.push({ field: f, before: existing[f] ?? "", after });
  }
  const addressChanged = changes.some((c) => c.field === "rawAddress");
  return { action: changes.length ? "update" : "same", changes, addressChanged };
}

/** 기존 학생을 고칠 때 실제로 쓸 값. set은 덮어쓸 칸, clear는 지울 칸(화면이 deleteField로 바꾼다) */
export function updatePayload(file: Partial<Student>, plan: RowPlan): { set: Record<string, unknown>; clear: string[] } {
  const set: Record<string, unknown> = {};
  const clear: string[] = [];
  for (const c of plan.changes) set[c.field] = c.after;
  if (plan.addressChanged) {
    for (const f of ADDRESS_TEXT_FIELDS) set[f] = (file[f] as string | undefined) ?? "";
    if (typeof file.lat === "number" && typeof file.lng === "number") {
      set.lat = file.lat;
      set.lng = file.lng;
      set.geocodeStatus = file.geocodeStatus || "ok";
      clear.push(...ADDRESS_GEO_EXTRA_FIELDS); // 옛 주소의 AI 검토 흔적·후보는 새 주소와 무관하다(코덱스 검토)
    } else {
      // 새 주소인데 좌표가 없다 — 옛 좌표를 남기면 옛 집으로 간다. 지우고 '주소 확인 대기'로 보낸다
      clear.push(...ADDRESS_GEO_FIELDS);
    }
    // 권역 안/밖 판정은 주소에 딸린 값이라 파일 값으로 다시 쓴다(사장이 '외진 골목'으로 돌린 택배 표시는 주소가 안 바뀌면 유지된다)
    if (typeof file.courierFlag === "boolean") set.courierFlag = file.courierFlag;
  }
  return { set, clear };
}

/** 이번 파일에 없는 기존 학생 — 삭제하지 않고 보여만 준다(전출인지 누락인지는 사람이 안다) */
export function missingFromFile<T extends { id: string; status?: string }>(existingOfSchools: T[], plannedIds: Set<string>): T[] {
  return existingOfSchools.filter((s) => !plannedIds.has(s.id) && s.status !== "paused");
}

/** 바뀐 칸을 사람 말로 */
export function describeChange(c: FieldChange): string {
  const label: Record<FileField, string> = { guardian: "보호자", phone: "전화", rawAddress: "주소", boxesPerMonth: "수량" };
  return `${label[c.field]} ${String(c.before || "(없음)")} → ${String(c.after)}`;
}
